import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import '../../components/NestModal/NestModal.css';
import './ExplodeNestedModal.css';

function stripHtml(html) {
  if (!html || typeof html !== 'string') return '';
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

/** True if exploding the wrapper mission would drop user-authored content on that mission. */
export function missionHasExplodableArtifacts(task) {
  if (!task) return false;
  if (task.attachmentCount > 0) return true;
  if (Array.isArray(task.deliverables) && task.deliverables.length > 0) return true;
  if (Array.isArray(task.inputs) && task.inputs.length > 0) return true;
  if (task.description && String(task.description).trim()) return true;
  if (stripHtml(task.notes || '').length > 0) return true;
  return false;
}

/**
 * Confirmation before exploding a mission: lifts nested campaign missions to the parent graph.
 */
const ExplodeNestedModal = ({
  isOpen,
  missionName = '',
  innerMissionCount = 0,
  willLoseArtifacts = false,
  onConfirm,
  onCancel,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onCancel?.();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel]);

  const handleConfirm = useCallback(() => {
    onConfirm?.();
  }, [onConfirm]);

  if (!isOpen) return null;

  return createPortal(
    <div className="nest-modal__overlay" onClick={onCancel}>
      <div className="nest-modal explode-nested-modal" onClick={(e) => e.stopPropagation()}>
        <div className="nest-modal__header">
          <div className="nest-modal__icon">💥</div>
          <div className="nest-modal__title">Explode nested campaign?</div>
        </div>
        <p className="explode-nested-modal__lede">
          <strong>{missionName || 'This mission'}</strong>
          {innerMissionCount > 0
            ? ` will be replaced by its ${innerMissionCount} inner mission${innerMissionCount === 1 ? '' : 's'}, connected into this campaign.`
            : ' will be removed.'}
        </p>
        <div className="explode-nested-modal__warning" role="alert">
          <strong>Data on this mission will be lost:</strong>
          {' '}documentation, notes, deliverables, inputs, and attachments that live on the mission
          you are exploding (not on missions inside the nested campaign) cannot be recovered after
          you confirm.
          {willLoseArtifacts ? (
            <span className="explode-nested-modal__warning--emphasis">
              {' '}
              It looks like this mission has some of that content today.
            </span>
          ) : null}
        </div>
        <div className="nest-modal__actions">
          <button type="button" className="nest-modal__btn nest-modal__btn--cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="nest-modal__btn nest-modal__btn--confirm" onClick={handleConfirm}>
            Explode
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ExplodeNestedModal;
